/* eslint-disable @next/next/no-img-element */
// HERO/BUSCA "Editorial com card" (modelo_busca = 7): seção full-bleed com imagem de fundo
// cover + overlay escuro à esquerda. Desktop = 2 colunas (ESQ: H1 grande a partir da frase do
// site + CTA outline pra listagem; DIR: card branco de busca). Mobile = 1 coluna (texto em
// cima, card abaixo). O card de busca consome as MESMAS fontes de dados das buscas atuais
// (allTypes/allCities/allTransacao) e navega com a MESMA URL da busca padrão (renderPath).
// TUDO VARIÁVEL: H1 = site.frase_busca; chips = tipos reais do tenant; finalidade = transações
// reais; cores = site.corN com contraste adaptativo. Nenhum texto/cor hardcoded.
// Default export = ErrorBoundary com fallback pro hero padrão (igual ao WelcomeImersivo).
import { hifenUrl } from '../../../utils/hifenUrl';
import { useRouter } from 'next/router';
import React, { useCallback, useMemo, useState } from 'react';
import { useCidadeFixaBusca } from '../../../utils/cidadeFixaBusca';
import { ensureReadableTextColor, isColorLight, removerAcentos } from '../../../utils/FunctionsGlobals';
import { genMetaEventId, trackMetaEvent } from '../../../utils/metaPixel';
import { appendCodsiteQuery } from '../../../utils/url';
import { getHeroVideoPoster, useHeroVideoReady } from '../../../utils/heroVideo';
import DrawerComponent from '../../drawer/DrawerComponent';
import CardsBusca from '../../shared/cards-busca/CardsBusca';
import styles from './WelcomeEditorial.module.css';
import { usePreviaBuilder } from '../../../lib/previaBuilder';

interface IWelcomeEditorial {
    allCities: Array<{ label: string; value: any }>;
    allTypes: Array<{ label: string; value: any }>;
    allTransacao: Array<{ label: string; value: string }>;
    site: any;
    urlBanner?: string;
    origin?: any;
    codsite?: any;
    codsite2?: any;
    valores?: any;
    setActiveDrawer?: any;
    activeDrawer?: any;
    favorito?: Array<number>;
    props?: any;
    allCards?: any; // atalhos de busca cadastrados no CRM (tbl_card_busca)
    fallback?: React.ReactNode;
}

const encodeSegment = (label: unknown, codsite?: unknown): string => {
    const raw = String(label ?? '').trim();
    if (!raw) return '';
    return hifenUrl(raw, codsite);
};

const transacaoLabel = (raw: string): string => {
    const low = String(raw || '').toLowerCase();
    if (low === 'venda') return 'Comprar';
    if (low === 'aluguel') return 'Alugar';
    if (low === 'temporada') return 'Temporada';
    return raw;
};

const IconPin = () => (
    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
        <circle cx="12" cy="10" r="3" />
    </svg>
);
const IconCity = () => (
    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M3 21h18M5 21V7l7-4v18M19 21V11l-7-4" />
        <path d="M9 9v.01M9 12v.01M9 15v.01M9 18v.01" />
    </svg>
);
const IconHash = () => (
    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <line x1="4" y1="9" x2="20" y2="9" />
        <line x1="4" y1="15" x2="20" y2="15" />
        <line x1="10" y1="3" x2="8" y2="21" />
        <line x1="16" y1="3" x2="14" y2="21" />
    </svg>
);

const TODOS = '__todos';

const WelcomeEditorialInner: React.FC<IWelcomeEditorial> = ({
    allCities,
    allTypes,
    allTransacao,
    site,
    urlBanner,
    origin,
    codsite2,
    setActiveDrawer,
    activeDrawer,
    favorito,
    props,
    allCards,
}) => {
    const { videoParado } = usePreviaBuilder(); // prévia dos cards do CRM: vídeo parado na capa
    const router = useRouter();
    const transacoes = allTransacao?.length ? allTransacao : [{ label: 'Venda', value: 'venda' }];
    const primeiraTransacao = String(transacoes[0]?.value || 'venda').toLowerCase();

    // finalidade: value da transação, ou TODOS quando o tenant opera >1 transação.
    const [finalidade, setFinalidade] = useState<string>(primeiraTransacao);
    const [tipos, setTipos] = useState<string[]>([]); // multi-select de tipos (values)
    const [bairro, setBairro] = useState<string>('');
    const [cidade, setCidade] = useState<string>('');
    const [codigo, setCodigo] = useState<string>('');
    const [loading, setLoading] = useState(false);

    // Cidade fixa na busca (CRM → Comportamento): aqui o campo é texto com <datalist>,
    // então o que entra é o NOME da cidade (o resolveCidadeLabel casa com o label do tenant).
    const aplicarCidadeFixa = useCallback((c: any) => setCidade(String(c?.label ?? '')), []);
    useCidadeFixaBusca(site?.cidade_busca, allCities, Boolean(cidade), aplicarCidadeFixa);

    const bannerSrc = urlBanner && urlBanner !== 'undefined' ? urlBanner.replaceAll('"', '') : '';
    // Mídia de fundo: vídeo do tenant > banner > gradiente (mesma prioridade dos heros 1/2/3).
    const videoSrc = site?.url_video ? String(site.url_video).replaceAll('"', '') : '';
    const heroPoster = getHeroVideoPoster(site?.url_video);
    const heroVideoReady = useHeroVideoReady();

    const buttonBackground = site?.cor2 || '#1e1e1e';
    const buttonTextColor =
        ensureReadableTextColor(isColorLight(site?.cor2) ? site?.cor13 : site?.cor11, buttonBackground) || '#ffffff';

    const headline = useMemo(() => {
        if (site?.frase_busca) {
            return String(site.frase_busca)
                .replace(/<[^>]+>/g, ' ')
                .replace(/\s+/g, ' ')
                .trim();
        }
        return 'Para quem busca mais do que um endereço';
    }, [site?.frase_busca]);

    const isTodos = finalidade === TODOS;
    const transacaoEfetiva = isTodos ? primeiraTransacao : finalidade;

    const toggleTipo = (value: string) => {
        setTipos((prev) => (prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]));
    };

    const limparFiltros = () => {
        setTipos([]);
        setBairro('');
        setCidade('');
        setCodigo('');
        setFinalidade(transacoes.length > 1 ? TODOS : primeiraTransacao);
    };

    // resolve o segmento de cidade a partir do texto digitado — casa com um label do tenant
    // se existir (autocomplete via <datalist>), senão usa o texto cru (a listagem resolve por label).
    const resolveCidadeLabel = (): string => {
        const txt = cidade.trim();
        if (!txt) return '';
        const match = allCities?.find((c) => String(c.label).toLowerCase() === txt.toLowerCase());
        return match ? String(match.label) : txt;
    };

    const buildUrl = (): string => {
        const cidadeLabel = resolveCidadeLabel();
        const tipoLabels = (tipos || [])
            .map((v) => allTypes?.find((t) => String(t.value) === String(v))?.label)
            .filter(Boolean) as string[];

        const citySegment = cidadeLabel ? encodeSegment(cidadeLabel, site?.id) : '-';
        const bairroSegment = bairro.trim() ? encodeSegment(bairro.trim(), site?.id) : '-';
        const tipoSegment = tipoLabels.length ? encodeSegment(tipoLabels.join('+'), site?.id) : '-';
        const codigoSegment = codigo.trim() ? `/${codigo.trim().replaceAll(' ', '%20')}` : '';

        const vazio = !cidadeLabel && !bairro.trim() && !tipoLabels.length && !codigo.trim();
        let path = `/imoveis/${transacaoEfetiva}/${citySegment}/${bairroSegment}/-/${tipoSegment}${codigoSegment}`;
        if (vazio || isTodos) path = `${path}?ordem=desc-inclusao`;
        path = appendCodsiteQuery(path, codsite2, origin);
        return removerAcentos(path).toLowerCase().replaceAll(' ', '-');
    };

    const onSubmit = (event: React.FormEvent) => {
        event.preventDefault();
        if (loading) return;
        setLoading(true);
        const cidadeLabel = resolveCidadeLabel();
        const tipoNomes = (tipos || [])
            .map((v) => allTypes?.find((t) => String(t.value) === String(v))?.label)
            .filter(Boolean)
            .join(', ');
        const searchString = [transacaoLabel(transacaoEfetiva), tipoNomes, cidadeLabel, bairro].filter(Boolean).join(' | ');
        const eventId = genMetaEventId();
        try { sessionStorage.setItem('meta:searchEventId', JSON.stringify({ id: eventId, at: Date.now() })); } catch { /* sessionStorage indisponível — Search sai sem dedup c/ CAPI */ }
        trackMetaEvent(site, 'Search', {
            search_string: searchString,
            transaction_type: transacaoEfetiva,
            property_type: tipoNomes,
            city: cidadeLabel,
            neighborhood: bairro,
        }, { eventId });
        router.push(buildUrl()).catch(() => setLoading(false));
    };

    const ctaUrl = removerAcentos(
        appendCodsiteQuery(`/imoveis/${primeiraTransacao}/-/-/-/-?ordem=desc-inclusao`, codsite2, origin)
    )
        .toLowerCase()
        .replaceAll(' ', '-');

    const finalidadeOptions = [
        ...transacoes.map((t) => ({ value: String(t.value).toLowerCase(), label: transacaoLabel(t.value) })),
        ...(transacoes.length > 1 ? [{ value: TODOS, label: 'Ver todos' }] : []),
    ];

    return (
        <section className={styles.hero}>
            <div className={styles.media}>
                {videoSrc ? (
                    <video
                        className={styles.mediaImg}
                        autoPlay={!videoParado}
                        muted
                        loop
                        playsInline
                        preload="none"
                        poster={heroPoster}
                        src={heroPoster ? (heroVideoReady ? videoSrc : undefined) : videoSrc}
                    >
                        <track kind="captions" label="Legenda" src="legenda.vtt" />
                    </video>
                ) : bannerSrc ? (
                    <img className={styles.mediaImg} src={bannerSrc} alt="Banner" />
                ) : (
                    <div className={styles.mediaGradient} aria-hidden="true" />
                )}
                <div className={styles.overlay} aria-hidden="true" />
                <div className={styles.glow} aria-hidden="true" />
            </div>

            <DrawerComponent
                site={site}
                activeDrawer={activeDrawer}
                setActiveDrawer={setActiveDrawer}
                favorito={favorito || []}
                menus={props?.menus}
            />

            <div className={styles.body}>
                {/* ESQUERDA: frase + CTA */}
                <div className={styles.copy}>
                    <h1 className={styles.headline}>{headline}</h1>
                    {site?.frase_busca_2 ? (
                        <p className={styles.subHeadline}>{String(site.frase_busca_2).replace(/<[^>]+>/g, ' ')}</p>
                    ) : null}
                    <a
                        href={ctaUrl}
                        className={styles.cta}
                        data-no-quicklink
                        data-no-instant
                        data-no-prefetch
                        onClick={() => trackMetaEvent(site, 'ViewContent', { content_name: 'Ver imóveis', content_category: 'property_search' })}
                    >
                        Ver imóveis
                    </a>
                </div>

                {/* DIREITA: card de busca */}
                {/* id="myLink": âncora do botão "BUSCA AVANÇADA" do topo (topo2). */}
                <form id="myLink" className={styles.searchCard} onSubmit={onSubmit}>
                    <span className={styles.cardTitle}>O que você está buscando?</span>

                    {allTypes?.length ? (
                        <div className={styles.chips} role="group" aria-label="Tipo de imóvel">
                            {allTypes.map((t) => {
                                const value = String(t.value);
                                const active = tipos.includes(value);
                                return (
                                    <button
                                        key={value}
                                        type="button"
                                        aria-pressed={active}
                                        className={`${styles.chip} ${active ? styles.chipActive : ''}`}
                                        style={active ? { background: buttonBackground, color: buttonTextColor, borderColor: buttonBackground } : {}}
                                        onClick={() => toggleTipo(value)}
                                    >
                                        {t.label}
                                    </button>
                                );
                            })}
                        </div>
                    ) : null}

                    <div className={styles.finalidade} role="radiogroup" aria-label="Finalidade">
                        {finalidadeOptions.map((opt) => {
                            const active = finalidade === opt.value;
                            return (
                                <label key={opt.value} className={styles.radioItem}>
                                    <input
                                        type="radio"
                                        name="finalidade-editorial"
                                        className={styles.radioInput}
                                        checked={active}
                                        onChange={() => setFinalidade(opt.value)}
                                    />
                                    <span
                                        className={styles.radioBox}
                                        style={active ? { background: buttonBackground, borderColor: buttonBackground } : {}}
                                        aria-hidden="true"
                                    />
                                    <span className={styles.radioLabel}>{opt.label}</span>
                                </label>
                            );
                        })}
                    </div>

                    <label className={styles.field}>
                        <span className={styles.fieldIcon} aria-hidden="true"><IconPin /></span>
                        <input
                            className={styles.input}
                            type="text"
                            value={bairro}
                            onChange={(e) => setBairro(e.target.value)}
                            placeholder="Qual bairro?"
                            aria-label="Bairro"
                        />
                    </label>
                    <label className={styles.field}>
                        <span className={styles.fieldIcon} aria-hidden="true"><IconCity /></span>
                        <input
                            className={styles.input}
                            type="text"
                            value={cidade}
                            onChange={(e) => setCidade(e.target.value)}
                            placeholder="Qual cidade?"
                            aria-label="Cidade"
                            list="editorial-cidades"
                        />
                        <datalist id="editorial-cidades">
                            {(allCities || []).map((c) => (
                                <option key={String(c.value)} value={String(c.label)} />
                            ))}
                        </datalist>
                    </label>
                    <label className={styles.field}>
                        <span className={styles.fieldIcon} aria-hidden="true"><IconHash /></span>
                        <input
                            className={styles.input}
                            type="text"
                            value={codigo}
                            onChange={(e) => setCodigo(e.target.value)}
                            placeholder="Código do imóvel"
                            aria-label="Código do imóvel"
                        />
                    </label>

                    <button type="button" className={styles.clear} onClick={limparFiltros}>
                        Limpar filtros
                    </button>

                    <button
                        type="submit"
                        className={styles.submit}
                        style={{ background: buttonBackground, color: buttonTextColor }}
                        disabled={loading}
                        aria-label="Encontrar imóveis"
                    >
                        <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
                            <circle cx="11" cy="11" r="7" />
                            <line x1="21" y1="21" x2="16.65" y2="16.65" />
                        </svg>
                        {loading ? 'Buscando...' : 'Encontrar imóveis'}
                    </button>
                </form>

                    <CardsBusca cards={allCards} site={site} variante="claro" />
                </div>
        </section>
    );
};

class WelcomeEditorial extends React.Component<IWelcomeEditorial, { hasError: boolean }> {
    constructor(props: IWelcomeEditorial) {
        super(props);
        this.state = { hasError: false };
    }
    static getDerivedStateFromError() {
        return { hasError: true };
    }
    componentDidCatch() {
        // silencioso: degrada pro hero padrão sem quebrar a home.
    }
    render() {
        const { fallback, site } = this.props;
        if (this.state.hasError || !site) return fallback ?? null;
        return <WelcomeEditorialInner {...this.props} />;
    }
}

export default WelcomeEditorial;
